import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from '@shared/core';
import type { ButtonV2Props } from '@withjoy/joykit';
import { Box, ButtonV2, Flex, FormControl, InputV2, TextV2 } from '@withjoy/joykit';
import { Category } from '@graphql/generated-base';
import { Footer } from '@apps/guest/packages/layout-engine/components/Footer';
import { GuestSiteTypographyOverride } from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride';
import type { OverrideTypography } from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride.types';
import { useUnlockDialogContext } from '../UnlockDialog/UnlockDialog.context';
import { useColorPaletteProvider } from '@apps/guest/packages/layout-engine/layouts/ColorPaletteProvider/ColorPaletteProvider';
import { useUnlockController } from '../UnlockDialog/shared/useUnlockController';
import { UnlockDialogThemeProvider } from '../UnlockDialog/UnlockDialogTheme.context';
import { useUnlockDialogInputStyles, useUnlockDialogLinkStyles } from '../UnlockDialog/useUnlockDialogButtonStyles';
import { StyledInputWrapper } from '../UnlockDialog/shared/UnlockInputWrapper.styles';
import { UnlockSubmitButton } from '../UnlockDialog/shared/UnlockSubmitButton';
import { withWindow } from '@shared/utils/withWindow';
import { AskHostForm } from '../UnlockDialog/AskHostForm';
import { AskHostSuccessForm } from '../UnlockDialog/AskHostSuccessForm';

const eventTitleTypographyOverride: OverrideTypography = [
  { variant: 'display6', category: Category.EVENT_TITLE },
  { variant: 'display4', category: Category.EVENT_TITLE },
];

// Matches the delay used in useDeferredInputFocus — allows iOS to treat programmatic
// focus as user-initiated and correctly scroll the input above the keyboard.
const INPUT_FOCUS_DELAY_MS = 300;

type WelcomePagePasswordGateProps = Readonly<{
  eventId: string;
  eventDisplayName?: string;
  showEventName?: boolean;
}>;

type View = 'password' | 'ask-host' | 'ask-host-success';

const LinkButton: React.FC<ButtonV2Props> = props => {
  const linkStyles = useUnlockDialogLinkStyles();
  return (
    <ButtonV2
      variant="ghost"
      intent="neutral"
      typographyVariant="button2"
      width="fit-content"
      height="fit-content"
      padding={0}
      borderBottom="1px solid"
      borderBottomColor="currentColor"
      borderBottomLeftRadius="0px"
      borderBottomRightRadius="0px"
      {...linkStyles}
      {...props}
    />
  );
};

const PasswordForm: React.FC<{ eventId: string; onShowAskHost: () => void }> = ({ eventId, onShowAskHost }) => {
  const { tq } = useTranslation('guestUnlock');
  const { markUnlocked } = useUnlockDialogContext();
  const inputStyles = useUnlockDialogInputStyles();

  // useDeferredInputFocus gates on isOpen from dialog context, which never fires here.
  // Focus on mount directly instead.
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    const timer = setTimeout(() => inputRef.current?.focus(), INPUT_FOCUS_DELAY_MS);
    return () => clearTimeout(timer);
  }, []);

  const onUnlockSuccess = () => {
    markUnlocked();
    withWindow(w => w.location.reload());
  };

  const { formik, loading, inputError, onSignInHandler } = useUnlockController({ eventId, onUnlockSuccess });

  return (
    <form
      onSubmit={formik.handleSubmit}
      data-testid="welcome-page-password-form"
    >
      <StyledInputWrapper $focusColor={inputStyles.focusColor}>
        <FormControl
          label={tq`Event Password`}
          overrides={{ Label: { props: { color: 'inherit' } } }}
        >
          <InputV2
            ref={inputRef}
            overrides={{ Root: { props: { borderColor: inputStyles.borderColor } } }}
            placeholder={tq`eg. xy4686`}
            isInvalid={(inputError && formik.touched.password) || undefined}
            isDisabled={loading}
            {...formik.getFieldProps('password')}
          />
        </FormControl>
      </StyledInputWrapper>
      <UnlockSubmitButton
        shape="rounded"
        width="100%"
        variant="outline"
        type="submit"
        loading={loading}
        marginTop={6}
      >
        {tq`View Event`}
      </UnlockSubmitButton>
      <Flex
        justifyContent="center"
        alignItems="center"
        gap={3}
        marginTop={6}
      >
        <LinkButton onClick={onShowAskHost}>{tq`Ask the Host`}</LinkButton>
        <TextV2 typographyVariant="label3">·</TextV2>
        <LinkButton onClick={onSignInHandler}>{tq`Sign In`}</LinkButton>
      </Flex>
    </form>
  );
};

type GateInnerProps = Readonly<{
  eventId: string;
  eventDisplayName?: string;
  showEventName: boolean;
}>;

const GateInner: React.FC<GateInnerProps> = ({ eventId, eventDisplayName, showEventName }) => {
  const { tq } = useTranslation('guestUnlock');
  const [view, setView] = useState<View>('password');

  return (
    <>
      {view === 'password' && (
        <>
          {showEventName && eventDisplayName && (
            <GuestSiteTypographyOverride override={eventTitleTypographyOverride}>
              <TextV2
                typographyVariant={{ _: 'display4', sm2: 'display6' }}
                textAlign="center"
                color="inherit"
                marginBottom={8}
                display="block"
              >
                {eventDisplayName}
              </TextV2>
            </GuestSiteTypographyOverride>
          )}
          <PasswordForm
            eventId={eventId}
            onShowAskHost={() => setView('ask-host')}
          />
        </>
      )}
      {view === 'ask-host' && (
        <>
          <AskHostForm
            eventId={eventId}
            onClose={() => setView('ask-host-success')}
            disablePadding
            focusOnMount
          />
          <Flex
            justifyContent="center"
            marginTop={6}
            marginBottom={6}
          >
            <LinkButton onClick={() => setView('password')}>{tq`Back`}</LinkButton>
          </Flex>
        </>
      )}
      {view === 'ask-host-success' && <AskHostSuccessForm onClose={() => setView('password')} />}
    </>
  );
};

export const WelcomePagePasswordGate: React.FC<WelcomePagePasswordGateProps> = ({ eventId, eventDisplayName, showEventName = false }) => {
  const { pageColor, pageFontColor } = useColorPaletteProvider();
  const resolvedPageColor = pageColor || '#ffffff';
  const resolvedFontColor = pageFontColor || '#000000';

  return (
    <UnlockDialogThemeProvider value={{ pageFontColor: resolvedFontColor }}>
      <Flex
        minHeight="100vh"
        style={{ minHeight: '100dvh' }}
        width="100%"
        flexDirection="column"
        backgroundColor={resolvedPageColor}
        color={resolvedFontColor}
        data-testid="welcome-page-password-gate"
      >
        <Flex
          flex="1"
          alignItems="center"
          justifyContent="center"
          paddingX={6}
        >
          <Box
            width="100%"
            maxWidth="400px"
          >
            <GateInner
              eventId={eventId}
              eventDisplayName={eventDisplayName}
              showEventName={showEventName}
            />
          </Box>
        </Flex>
        <Box
          marginTop="auto"
          paddingBottom={{ _: 6, sm2: 8 }}
          width="100%"
          display="flex"
          justifyContent="center"
        >
          <Footer />
        </Box>
      </Flex>
    </UnlockDialogThemeProvider>
  );
};
